import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useAuth } from "../utils/AuthContext";
import {
  fonts,
  getStartedModalCloseIcon,
  getStartedModalStyles,
  getStartedSmallButtons,
  getStartedTexts,
  headingFonts,
  mainButtonStyles,
  textFieldStyles,
} from "../../Styles/Styles";

export default function LoginForm({ onClose }) {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    identifier: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value });
  };

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Step 1: Fetch the IP address
      const ipRes = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipRes.json();
      const ipAddress = ipData.ip;

      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/user/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            emailOrUsername: credentials.identifier,
            password: credentials.password,
            ipAddress,
          }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        login(data.token, data.user);
        onClose();
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ ...getStartedModalStyles }}>
      {/* Close Button (Top Right) */}
      <IconButton
        onClick={() => onClose("buttons")}
        sx={{ ...getStartedModalCloseIcon }}
      >
        <CancelOutlinedIcon />
      </IconButton>
      <Typography
        variant="h5"
        mb={1}
        fontWeight="bold"
        sx={{
          ...headingFonts,
          color: "var(--secondary-color)",
        }}
      >
        Welcome Back
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        mb={2}
        sx={{ ...headingFonts }}
      >
        Please log in to your account
      </Typography>

      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <TextField
          label="Email or Username"
          name="identifier"
          value={credentials.identifier}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          variant="outlined"
          sx={{ ...textFieldStyles }}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          name="password"
          value={credentials.password}
          onChange={handleChange}
          fullWidth
          required
          margin="normal"
          variant="outlined"
          sx={{ ...textFieldStyles }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          className="main-button-style"
          sx={{ mt: 2, ...mainButtonStyles }}
          disabled={loading}
        >
          {loading ? (
            <>
              <span>Logging In...</span>
              <CircularProgress size={20} sx={{ color: "white" }} />
            </>
          ) : (
            <>
              <span>Log In</span>
              <ArrowForwardOutlinedIcon fontSize="small" />
            </>
          )}
        </Button>
      </form>

      {error && (
        <Alert severity="error" sx={{ ...fonts, mt: 2, width: "100%" }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" sx={{ mt: 2, ...getStartedTexts }}>
        Don't have an account?{" "}
        <Button
          size="small"
          sx={{ ...getStartedSmallButtons }}
          onClick={() => onClose("register")}
        >
          Register Now
        </Button>
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "gray",
          fontFamily: "ClashGrotesk-Bold",
          letterSpacing: 1,
          display: "flex",
          alignItems: "center",
          textAlign: "left",
          gap: "4px",
        }}
      >
        OR
      </Typography>
      <Typography variant="body2" sx={{ ...getStartedTexts }}>
        Forgot your password?{" "}
        <Button
          size="small"
          sx={{ ...getStartedSmallButtons }}
          onClick={() => onClose("forgot-password")}
        >
          Reset it here
        </Button>
      </Typography>
    </Box>
  );
}
